import { publicAsset } from "@/lib/publicAsset";
import { getWeaponSpec } from "./weaponSpecs";

const ICON_ASSET_VERSION = "vip-icons-2026-05-20-safe-aliases";

/** Pasta no repositório (manifest). */
export const ARMAS_ITENS_SOURCE_SUBFOLDER = "itens de armas";
/** Pasta em public/ (sem espaços — URLs estáveis no dev server). */
export const ARMAS_ITENS_PUBLIC_SUBFOLDER = "itens-de-armas";

export function isItensDeArmasPath(file) {
    if (!file) return false;
    return /itens[- ]de[- ]armas\//i.test(String(file).replace(/\\/g, "/"));
}

function encodeIconPath(folder, file) {
    const encFolder = String(folder)
        .split("/")
        .map((seg) => encodeURIComponent(seg))
        .join("/");
    const enc = String(file)
        .replace(/\\/g, "/")
        .split("/")
        .map((seg) => encodeURIComponent(seg))
        .join("/");
    return `${publicAsset(`/images/icons/${encFolder}/${enc}`)}?v=${ICON_ASSET_VERSION}`;
}

function slugifyAssetSegment(value) {
    return String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Za-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
}

function safeAssetPath(file) {
    const parts = String(file).replace(/\\/g, "/").split("/");
    const last = parts.pop() || "";
    const dot = last.lastIndexOf(".");
    const stem = dot >= 0 ? last.slice(0, dot) : last;
    const ext = dot >= 0 ? last.slice(dot).toLowerCase() : "";
    return [...parts.map(slugifyAssetSegment), `${slugifyAssetSegment(stem)}${ext}`].join("/");
}

/**
 * Caminho relativo do ícone dentro da pasta (evita subpastas com espaços na URL).
 * Em Armas, usa o ficheiro na raiz quando existir duplicado em `itens de armas/`.
 */
export function normalizeIconFile(folder, file) {
    if (!file) return "";
    const normalized = String(file).replace(/\\/g, "/");
    if (folder !== "Armas") return normalized;
    if (isItensDeArmasPath(normalized)) {
        return toPublicItensDeArmasPath(normalized);
    }
    const base = normalized.split("/").pop();
    if (!base) return normalized;
    if (normalized.includes("/")) return base;
    return normalized;
}

/** Converte caminho fonte `itens de armas/` → `itens-de-armas/` (public). */
export function toPublicItensDeArmasPath(file) {
    return String(file)
        .replace(/\\/g, "/")
        .replace(/itens de armas\//gi, `${ARMAS_ITENS_PUBLIC_SUBFOLDER}/`);
}

/** Ícone de acessório em subpasta `itens de armas/`. */
export function itensDeArmasAttachmentIconUrl(sourcePath) {
    if (!sourcePath) return "";
    return encodeIconPath("Armas", toPublicItensDeArmasPath(sourcePath));
}

/** Variante com pasta `itens de armas/` (alguns deploys só têm esta cópia). */
export function itensDeArmasAttachmentIconUrlSpaced(sourcePath) {
    if (!sourcePath) return "";
    const normalized = String(sourcePath).replace(/\\/g, "/");
    if (!/itens de armas\//i.test(normalized)) return "";
    return encodeIconPath("Armas", normalized);
}

/** Ícones em `public/images/icons/{pasta}/` (fonte: `images/icons/`). */
export function iconAssetUrl(folder, file) {
    if (!folder || !file) return "";
    return encodeIconPath(folder, normalizeIconFile(folder, file));
}

/**
 * URL estável para preview/kit (pastas e ficheiros slug — evita 404 no dev server).
 * @param {string} folder
 * @param {string} file
 */
export function iconAssetUrlSafe(folder, file) {
    if (!folder || !file) return "";
    const fileNorm = String(file).replace(/\\/g, "/");
    const fileSafe = safeAssetPath(fileNorm);
    const folderSlug = slugifyAssetSegment(folder);
    return (
        encodeIconPath(folderSlug, fileSafe) ||
        encodeIconPath(folder, fileSafe) ||
        encodeIconPath(folderSlug, fileNorm) ||
        encodeIconPath(folder, fileNorm)
    );
}

/**
 * Primária slug + fallbacks (nome PT codificado) para `<img onError>`.
 * @param {string} folder
 * @param {string} file
 */
export function buildBonusKitIconCandidates(folder, file) {
    const normalized = String(file).replace(/\\/g, "/");
    const primary = iconAssetUrlSafe(folder, normalized);
    const rest = buildCatalogIconCandidates(folder, normalized, normalized, false).filter(
        (u) => u && u !== primary,
    );
    return [primary, ...rest].filter(Boolean);
}

/** Placeholders dos slots de roupa/armadura — `public/images/icones/` (fonte: `images/icones/`). */
const ARMOR_GHOST_VERSION = "armor-ghost-2026-05-20-v2";

export function armorGhostIconUrl(file) {
    if (!file) return "";
    const enc = encodeURIComponent(file);
    return `${publicAsset(`/images/icones/${enc}`)}?v=${ARMOR_GHOST_VERSION}`;
}

/**
 * URLs alternativas quando a primária falha (subpasta itens-de-armas, extensões, raiz Armas).
 * @param {string} folder
 * @param {string} iconFile
 * @param {string} [sourcePath]
 * @param {boolean} [subAttach]
 */
export function buildCatalogIconCandidates(folder, iconFile, sourcePath, subAttach) {
    const out = [];
    const push = (url) => {
        if (url) out.push(url);
    };
    const pushFile = (file) => push(iconAssetUrl(folder, file));

    if (subAttach && sourcePath) {
        push(itensDeArmasAttachmentIconUrl(sourcePath));
        push(itensDeArmasAttachmentIconUrlSpaced(sourcePath));
    }
    pushFile(iconFile);
    push(encodeIconPath(folder, safeAssetPath(iconFile)));
    push(encodeIconPath(slugifyAssetSegment(folder), iconFile));
    push(encodeIconPath(slugifyAssetSegment(folder), safeAssetPath(iconFile)));

    const base = String(iconFile || sourcePath || "")
        .replace(/\\/g, "/")
        .split("/")
        .pop();
    if (!base || folder !== "Armas") {
        return [...new Set(out)];
    }

    pushFile(`${ARMAS_ITENS_PUBLIC_SUBFOLDER}/${base}`);
    pushFile(base);
    pushFile(safeAssetPath(base));
    pushFile(`${ARMAS_ITENS_PUBLIC_SUBFOLDER}/${safeAssetPath(base)}`);
    push(encodeIconPath(slugifyAssetSegment(folder), safeAssetPath(base)));

    if (/\.avif$/i.test(base)) {
        const stem = base.replace(/\.avif$/i, "");
        pushFile(`${ARMAS_ITENS_PUBLIC_SUBFOLDER}/${stem}.webp`);
        pushFile(`${ARMAS_ITENS_PUBLIC_SUBFOLDER}/${stem}.png`);
        pushFile(`${stem}.webp`);
        pushFile(`${stem}.png`);
    }

    return [...new Set(out)];
}

/** Vista 3D no modal de arma — ficheiros em `Armas/itens de armas/`. */
export function weaponShowcaseImageUrl(iconFileOrWeaponKey) {
    const spec = getWeaponSpec(iconFileOrWeaponKey);
    const file = spec?.file || iconFileOrWeaponKey;
    if (!file || String(file).includes("/")) return "";
    return encodeIconPath("Armas", `${ARMAS_ITENS_PUBLIC_SUBFOLDER}/${file}`);
}
